import { getFormatDateFromStoreDate } from 'shared-components';
import { DocAccessType, IFollowUpDocuments } from '../../../../../types';
import { UserRole } from '../../../../../store/auth/types';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../../utilities/documents';
import * as React from 'react';
import FingerWithApprove from '../../../../atoms/finger-with-approve';
import { SummaryRow } from '../../../../Summary/SummaryRow';
import { Section } from '../../../../Finger/Section';
import NoDocuments from '../../../../atoms/no-doc-placeholder';
import { useTranslation } from "react-i18next";

enum ImmigrationStatusEnum {
  STUDENT = 'Student',
  SETTLED = 'Settled',
  PRE_SETTLED = 'Pre-settled',
  OTHER = 'Other',
}

interface IProps {
  userAccess?: DocAccessType;
  shareCodeDocuments: IFollowUpDocuments['beforeTravel']['immigrationStatusShareCode'];
  pdfCopyDocuments: IFollowUpDocuments['beforeTravel']['immigrationStatusPdfCopy'];
  dob?: string;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;

  onUploadClick: (docKey: string, docType: string, modalTitle: string) => void;
  onOpenClick: (docKey: string, docType: string) => void;
  onApproveClick: (docKey: string, docType: string) => void;
  onRejectClick: (docKey: string, docType: string, modalTitle: string) => void;
  onDeleteClick: (docKey: string, docType: string, modalTitle: string) => void;
}

export const ImmigrationStatus = (props: IProps) => {
    const { t } = useTranslation('common');
  const { shareCodeDocuments: shareCodeDocs, pdfCopyDocuments: pdfCopyDocs, userAccess, userRole } = props;

  const visibleShareCodeDocs = getVisible({ documentsHash: shareCodeDocs, userAccess, userRole });
  const visiblePdfCopyDocs = getVisible({ documentsHash: pdfCopyDocs, userAccess, userRole });

  const hasShareCodeDocs = visibleShareCodeDocs.length > 0;
  const hasPdfCopyDocs = visiblePdfCopyDocs.length > 0;

  const hasDocs = hasShareCodeDocs || hasPdfCopyDocs;
  const hasDeletedDocs = hasDeleted(shareCodeDocs) || hasDeleted(pdfCopyDocs);

  const docNodes: React.ReactNode[] = [];

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if (hasShareCodeDocs) {
    docNodes.push(
      ...visibleShareCodeDocs.map((shareCode: any, shareCodeIndex: number) => {
        const { key: docKey, item } = shareCode;

        return (
          <FingerWithApprove
            id={`immigration-status-share-code-${docKey}`}
            key={`immigration-status-share-code-${docKey}`}
            status={getFingerStatus(item)}
            isProcessingPdf={item.isProcessingPdf}
            fileNames={item.fileNames}
            reason={item.reason}
            label={t('common:immigrationStatusShareCode', { shareCodeIndex: Object.keys(shareCodeDocs ?? {}).length > 1 ? (shareCodeIndex > 0 ? shareCodeIndex + 1 : '') : '' })}
            canUpload={props.canUpload}
            canOpen={props.canOpen}
            canApprove={props.canApprove}
            canReject={props.canReject}
            isDeleting={props.isDeleting}
            canAcknowledge={false}
            onUploadClick={() => {
              props.onUploadClick(docKey, 'immigrationStatusShareCode', 'Immigration Status: Share Code');
            }}
            onOpenClick={() => {
              props.onOpenClick(docKey, 'immigrationStatusShareCode');
            }}
            onApproveClick={() => {
              props.onApproveClick(docKey, 'immigrationStatusShareCode');
            }}
            onRejectClick={() => {
              props.onRejectClick(docKey, 'immigrationStatusShareCode', 'Immigration Status: Share Code');
            }}
            onDeleteClick={() => {
              props.onDeleteClick(docKey, 'immigrationStatusShareCode', 'Immigration Status: Share Code');
            }}
            onAcknowledgeClick={() => {}}
            userRole={props.userRole}
            deletedByTTL={item.deletedByTTL}
            notDocumentFinger // share code does not require any doc to be uploaded (passing notDocumentFinger to hide the preview button)
          >
            {item.shareCode && (
              <>
                <SummaryRow
                  key={`share-code-code-${docKey}`}
                  label={t('common:shareCode')}
                  fullLabel="Share Code"
                  value={item.shareCode}
                />

                <SummaryRow
                  key={`share-code-dob-${docKey}`}
                  label={t('common:dateOfBirth')}
                  fullLabel="Date of Birth"
                  value={getFormatDateFromStoreDate(props.dob)}
                />

                <SummaryRow
                  key={`share-code-date-code-generate-${docKey}`}
                  label={t('common:dateCodeGenerated')}
                  fullLabel="When did you generate this share code?"
                  value={getFormatDateFromStoreDate(item.shareCodeGenerateDate)}
                />

                <SummaryRow
                  key={`share-code-date-immigrationStatus-${docKey}`}
                  label={t('common:status')}
                  fullLabel="Which Immigration status have you been granted?"
                  value={item.immigrationStatus}
                />

                {item.immigrationStatus === ImmigrationStatusEnum.STUDENT && (
                  <SummaryRow
                    key={`share-code-date-startDate-${docKey}`}
                    label={t('common:validFrom')}
                    fullLabel="What is the valid from date for this immigration status?"
                    value={getFormatDateFromStoreDate(item.startDate)}
                  />
                )}

                {[ImmigrationStatusEnum.STUDENT, ImmigrationStatusEnum.PRE_SETTLED].includes(
                  item.immigrationStatus,
                ) && (
                  <SummaryRow
                    key={`share-code-date-untilDate-${docKey}`}
                    label={t('common:validUntil')}
                    fullLabel="What is the valid until date for this immigration status?"
                    value={getFormatDateFromStoreDate(item.validUntil)}
                  />
                )}

                {item.immigrationStatus === ImmigrationStatusEnum.OTHER && (
                  <SummaryRow
                    key={`share-code-date-other-${docKey}`}
                    label={t('common:detailsOfStatusGranted')}
                    fullLabel="Please enter the status granted as well as any start or valid until dates for this status (if available) "
                    value={item.detailsOfStatusGranted}
                  />
                )}
              </>
            )}
          </FingerWithApprove>
        );
      }),
    );
  }

  if (hasPdfCopyDocs) {
    docNodes.push(
      ...visiblePdfCopyDocs.map((shareCode: any, shareCodeIndex: number) => {
        const { key: docKey, item } = shareCode;

        return (
          <FingerWithApprove
            id={`immigration-status-pdf-copy-${docKey}`}
            key={`immigration-status-pdf-copy-${docKey}`}
            status={getFingerStatus(item)}
            isProcessingPdf={item.isProcessingPdf}
            reason={item.reason}
            label={t('common:immigrationStatusPdfCopy', { shareCodeIndex: Object.keys(shareCodeDocs ?? {}).length > 1 ? (shareCodeIndex > 0 ? shareCodeIndex + 1 : '') : '' })}
            fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
            fileNames={item.fileNames}
            canUpload={props.canUpload}
            canOpen={props.canOpen}
            canApprove={props.canApprove}
            canReject={props.canReject}
            isDeleting={props.isDeleting}
            canAcknowledge={false}
            onUploadClick={() => {
              props.onUploadClick(docKey, 'immigrationStatusPdfCopy', 'Immigration Status: Pdf Copy');
            }}
            onOpenClick={() => {
              props.onOpenClick(docKey, 'immigrationStatusPdfCopy');
            }}
            onApproveClick={() => {
              props.onApproveClick(docKey, 'immigrationStatusPdfCopy');
            }}
            onRejectClick={() => {
              props.onRejectClick(docKey, 'immigrationStatusPdfCopy', 'Immigration Status: Pdf Copy');
            }}
            onDeleteClick={() => {
              props.onDeleteClick(docKey, 'immigrationStatusPdfCopy', 'Immigration Status: Pdf Copy');
            }}
            onAcknowledgeClick={() => {}}
            userRole={props.userRole}
            deletedByTTL={item.deletedByTTL}
          />
        );
      }),
    );
  }

  return (
    <>
      <Section key={`digital-immigration-status`} label={t('common:digitalImmigrationStatus')} canEdit={false} warning={''}>
        {hasDocs ? docNodes : <NoDocuments />}
      </Section>
    </>
  );
};
